import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import WhyBookWithUs from '../content/WhyBookWithUs';
import FeaturedCuisines from '../FeaturedCuisines';
import HowItWorks from '../content/HowItWorks';
import PizzaFaq from '../faqs/PizzaFaq';
import CateringCta from '../content/CateringCta';
import AnyEvent from '../content/AnyEvent';
import TripleCta from '../content/TripleCta';
import Testimonials from '../content/Testimonials';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

function PizzaCatering() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  const featuredTrucks = { food_categories: 'pizza' };

  return (
    <div className='CateringPage'>
      <Meta
        title={`Pizza ${vehicle_type} Catering in ${city_name}`}
        type='website'
        canonical='/food-truck-catering/pizza-catering'
        description={`Book pizza ${vehicleType} catering in ${city_name} for weddings, parties, and corporate events. Wood-fired & artisan pizza. Get free quotes from top pizza ${vehicleType}s.`}
        image='https://static.seattlefoodtruck.com/photos/pizza1.webp'
        structuredData={{
          cateringService: {
            name: `Pizza ${vehicle_type} Catering in ${city_name}`,
            description: `Book pizza ${vehicleType} catering in ${city_name} for weddings, parties, and corporate events.`,
            url: '/food-truck-catering/pizza-catering',
            serviceType: 'Pizza Truck Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Pizza Catering', item: '/food-truck-catering/pizza-catering' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/pizza1.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Pizza {vehicle_type} Catering for Your Next Event</h1>
            <p>Craving fresh, hot, and delicious pizza at your next event? Whether it&#39;s a <Link to='/food-truck-catering/wedding-catering'>wedding</Link>, <Link to='/food-truck-catering/corporate-catering'>corporate gathering</Link>, <Link to='/food-truck-catering/birthday-parties'>birthday party</Link>, or <Link to='/food-truck-catering/community-events'>festival</Link>, our network of top-rated pizza food {vehicleType}s will bring the perfect slice to your celebration. From classic Neapolitan to wood-fired artisanal creations, {site_title} makes it effortless to find and book the best pizza {vehicleType}s in town. Simply tell us about your event, receive tailored quotes, and secure your perfect match — it&#39;s that easy!</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Start Your Pizza Catering Request</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <TripleCta
        cuisine='pizza'
        image='https://static.seattlefoodtruck.com/photos/pizza2.webp'
        optionsText='From New York-style to Detroit deep dish, find the perfect pizza for your crowd.'
      />

      <FeaturedTrucks title={`Featured Pizza Food ${vehicle_type}s`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Book a Pizza Food ${vehicle_type} for Catering?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>Slice to Meet You: The Best Pizza Food {vehicle_type}s in {city_name}</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/pizza3.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Unmatched Pizza Selection</h4>
            <p>
              {site_title} connects you with {city_name}&#39;s best pizza food {vehicleType}s, offering a variety of styles — from wood-fired Neapolitan and crispy thin crust to deep-dish and creative fusion pies. Whether you&#39;re craving a classic Margherita or a gourmet truffle mushroom pizza, we&#39;ve got you covered. Our vendors use high-quality, fresh ingredients, and many offer locally sourced toppings to elevate the taste and experience. Some {vehicleType}s even provide gluten-free and vegan options, ensuring everyone at your event can enjoy a slice.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/pizza4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Seamless Booking Process</h4>
            <p>
              No need to call around or email multiple vendors. Our user-friendly platform allows you to enter your event details, receive multiple quotes from verified pizza food {vehicleType}s, and book in minutes. Every {vehicleType} in our network is vetted to ensure quality, reliability, and excellent customer service. Whether you need an entire pizza catering setup for a large wedding or just a fun pizza option for a small gathering, our process is designed to be as smooth and efficient as possible.
            </p>
          </div>
        </div>
      </section>

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/pizza5.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h4>Dedicated Event Support & Flexibility</h4>
            <p>
              We&#39;re not just a booking service — we&#39;re your event partner. Our team is dedicated to helping you create an unforgettable experience, no matter the size or type of event. Need last-minute adjustments? We&#39;ll work with vendors to accommodate changes. Not sure how much food you&#39;ll need? Our team can help estimate portions and recommend the right truck for your guest count. If a vendor has to cancel unexpectedly, we have a network of replacement pizza {vehicleType}s ready to step in so your event never misses a beat.
            </p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent title={`Pizza Food ${vehicle_type} Catering for Any Event`} />

      <Testimonials title="Join Thousands Who've Booked Pizza Catering With Us!" />

      <CostEstimator title='Pizza Catering Cost Estimator' />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <PizzaFaq />
      </section>

      <section className='Section--extraPadding u-pt2'>
        <CateringCta />
      </section>
    </div>
  );
}

export default PizzaCatering;
